import { observer } from "mobx-react-lite";
import { LoadingBar, PaginationControls } from "@/components/common";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import ConsolidatedMetricDetailRow from "./ConsolidatedMetricDetailRow";
import MetricDetailRowSkeleton from "./skeleton/MetricDetailRowSkeleton";
import { humanizeFormula } from "@/features/esg/utils/formula-display";

const ConsolidatedMetricDetailPanel = observer(({ panel }) => {
  const { t } = useTran();
  const {
    rows,
    total,
    disclosure,
    ratioBreakdown,
    collapsedChildren,
    years,
    page,
    pageSize,
    loading,
    hasLoadedOnce,
    setPage,
    setPageSize,
  } = panel;

  const showSkeleton = !hasLoadedOnce && loading;
  const showRefresh = hasLoadedOnce && loading;
  const unit = disclosure?.unit || "";
  const isGroupLevel =
    disclosure?.consolidationMethod === "PARENT_ONLY" ||
    disclosure?.consolidationMethod === "PASSTHROUGH";
  const hasStrays = rows.some((r) => r.status === "missing_at_parent");
  const hasSubEntries =
    Array.isArray(disclosure?.subEntries) &&
    disclosure.subEntries.length > 0 &&
    Boolean(disclosure?.formula);

  return (
    <div className="relative mb-3 mt-1 overflow-hidden rounded-md border border-border/40 bg-muted/20 p-3">
      {showRefresh && <LoadingBar />}
      <div
        className={cn(
          "space-y-3 transition-opacity",
          showRefresh && "opacity-60",
        )}
      >
        {!showSkeleton && isGroupLevel && hasStrays && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
            {t(
              "Sub-organisations have data, but this KPI is reported at group level. Enter the value at the parent company.",
            )}
          </div>
        )}
        {!showSkeleton && hasSubEntries && (
          <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">
              {t("Ratio components")}:
            </span>{" "}
            <span className="font-mono">
              {humanizeFormula(disclosure.formula)}
            </span>
          </div>
        )}
        <PaginationControls
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[5, 10, 20]}
          disabled={loading}
          listLabel={t("Organizations")}
          hideWhenSinglePage
        />
        {showSkeleton ? (
          <div className="space-y-2">
            {Array.from({ length: pageSize }).map((_, i) => (
              <MetricDetailRowSkeleton key={i} />
            ))}
          </div>
        ) : ratioBreakdown ? (
          <div className="rounded-md border border-dashed border-border/70 p-6 text-center text-sm text-muted-foreground">
            {t(
              "Ratio metric — per-org breakdown is shown via formula components.",
            )}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-border/70 p-6 text-center text-sm text-muted-foreground">
            {t("No organizations match your filters.")}
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((row) => (
              <ConsolidatedMetricDetailRow
                key={row.orgId}
                row={row}
                years={years}
                unit={unit}
                temporalAggregation={disclosure?.temporalAggregation}
                subEntries={hasSubEntries ? disclosure.subEntries : null}
              />
            ))}
            {collapsedChildren && (
              <div className="rounded-md border border-dashed border-border/40 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                {t("▸ {{count}} {{label}}", {
                  count: collapsedChildren.count,
                  label: t(collapsedChildren.label),
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default ConsolidatedMetricDetailPanel;
